import React, { useState, useEffect, useRef } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Puzzle, Lightbulb, Plus, X, AlertTriangle, Target, List, RefreshCw } from 'lucide-react';

const C1_ExtractConstructs = () => {
  const { dataC, updateDataRaw, nextStep } = useWizard();
  const isInitialized = useRef(false);
  
  const [researchAim, setResearchAim] = useState(dataC?.researchAim || '');
  const [objectives, setObjectives] = useState(
    dataC?.objectives?.length > 0 && dataC.objectives.some(o => o.trim()) 
      ? dataC.objectives 
      : ['', '', '', '', '', '', '']
  );
  const [constructs, setConstructs] = useState(dataC?.constructs || []);
  const [newConstruct, setNewConstruct] = useState({ name: '', type: 'independent' });
  const hasAutoPopulated = useRef(dataC?.constructs?.length > 0);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
  }, []);

  // Save constructs to context whenever they change
  useEffect(() => {
    if (isInitialized.current && constructs.length > 0) {
      updateDataRaw({ 'wizardC.constructs': constructs });
    }
  }, [constructs, updateDataRaw]);

  // Save research aim and objectives when they change
  useEffect(() => {
    if (isInitialized.current) {
      updateDataRaw({ 
        'wizardC.researchAim': researchAim,
        'wizardC.objectives': objectives 
      });
    }
  }, [researchAim, objectives, updateDataRaw]);

  const handleObjectiveChange = (index, value) => {
    const updated = [...objectives];
    updated[index] = value;
    setObjectives(updated);
  };

  const filledObjectivesCount = objectives.filter(o => o.trim()).length;

  const extractKeyPhrase = (text) => {
    const stopWords = ['the', 'a', 'an', 'to', 'of', 'in', 'on', 'for', 'and', 'or', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'shall', 'can', 'this', 'that', 'these', 'those', 'what', 'which', 'who', 'whom', 'whose', 'where', 'when', 'why', 'how', 'if', 'whether', 'determine', 'examine', 'investigate', 'explore', 'assess', 'analyze', 'study', 'evaluate', 'identify', 'understand', 'effect', 'impact', 'influence', 'role', 'relationship', 'between', 'affects', 'affect'];

    const limitToThreeWords = (phrase) => {
      const words = phrase.split(/\s+/).filter(w => w.length > 0);
      return words.slice(0, 3).join(' ');
    };

    const patterns = [
      /(?:effect|impact|influence|role)\s+of\s+([^,.\n]+?)(?:\s+on|\s+in|\s+among|$)/i,
      /(?:relationship|correlation|association)\s+between\s+([^,.\n]+?)\s+and\s+([^,.\n]+)/i,
      /(?:how|whether|if)\s+([^,.\n]+?)\s+(?:affects?|influences?|impacts?|relates?)/i,
      /(?:determine|examine|investigate|explore|assess|analyze)\s+(?:the\s+)?([^,.\n]+?)(?:\s+on|\s+in|\s+among|$)/i,
      /(?:on|affects?|influences?|impacts?)\s+([^,.\n]+?)(?:\s+in|\s+among|\s+within|$)/i,
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        const extracted = (match[2] || match[1]).trim();
        if (extracted.length > 2) {
          const words = extracted.split(/\s+/).filter(w => {
            const clean = w.toLowerCase().replace(/[^a-z]/g, '');
            return clean.length > 2 && !stopWords.includes(clean);
          });
          if (words.length > 0) {
            const phrase = words.slice(0, 3).join(' ').replace(/[.,;:!?]+$/, '');
            return phrase.charAt(0).toUpperCase() + phrase.slice(1).toLowerCase();
          }
        }
      }
    }

    const words = text.split(/\s+/).filter(w => {
      const clean = w.toLowerCase().replace(/[^a-z]/g, '');
      return clean.length > 2 && !stopWords.includes(clean);
    });

    if (words.length > 0) {
      const phrase = words.slice(0, 3).join(' ').replace(/[.,;:!?]+$/, '');
      return phrase.charAt(0).toUpperCase() + phrase.slice(1).toLowerCase();
    }

    return limitToThreeWords(text.substring(0, 30).trim());
  };

  const extractDefinition = (text, constructName) => {
    const cleaned = text.trim();
    if (cleaned.length >= 15) {
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }
    return `The concept of ${constructName.toLowerCase()} as described in the research objectives`;
  };

  const autoPopulateConstructs = () => {
    const filledObjectives = objectives.filter(o => o.trim());
    const newConstructs = filledObjectives.map((obj, index) => {
      const name = extractKeyPhrase(obj);
      return {
        id: Date.now() + index,
        name: name,
        type: 'independent',
        definition: extractDefinition(obj, name)
      };
    });
    setConstructs(newConstructs);
    hasAutoPopulated.current = true;
  };

  const addConstruct = () => {
    if (newConstruct.name.trim()) {
      const updated = [...constructs, { ...newConstruct, id: Date.now() }];
      setConstructs(updated);
      setNewConstruct({ name: '', type: 'independent' });
    }
  };

  const removeConstruct = (id) => {
    const updated = constructs.filter(c => c.id !== id);
    setConstructs(updated);
  };

  const updateConstructType = (id, newType) => {
    const updated = constructs.map(c => 
      c.id === id ? { ...c, type: newType } : c
    );
    setConstructs(updated);
  };

  const updateConstructName = (id, newName) => {
    const updated = constructs.map(c => 
      c.id === id ? { ...c, name: newName } : c
    );
    setConstructs(updated);
  };

  const updateConstructDefinition = (id, newDefinition) => {
    const updated = constructs.map(c => 
      c.id === id ? { ...c, definition: newDefinition } : c
    );
    setConstructs(updated);
  };

  const handleContinue = () => {
    updateDataRaw({ 
      'wizardC.researchAim': researchAim,
      'wizardC.objectives': objectives,
      'wizardC.constructs': constructs 
    });
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon constructs-icon">
          <Puzzle size={32} />
        </div>
        <div>
          <h2>Step 1: Research Aim & Objectives</h2>
          <p className="step-description">Define your research aim and objectives, then extract key constructs</p>
        </div>
      </div>

      <div className="step-content">
        <div className="research-aim-section">
          <div className="section-header">
            <Target size={20} />
            <h3>Research Aim</h3>
          </div>
          <p className="section-help">
            State the overall purpose of your research in one clear sentence.
          </p>
          <textarea
            className="research-aim-input"
            value={researchAim}
            onChange={(e) => setResearchAim(e.target.value)}
            placeholder="e.g., To investigate the impact of remote work on employee productivity and job satisfaction in the technology sector..."
            rows={3}
          />
        </div>

        <div className="research-objectives-section">
          <div className="section-header">
            <List size={20} />
            <h3>Research Objectives/Questions</h3>
            <span className="counter-badge">{filledObjectivesCount}/7</span>
          </div>
          
          <div className="objectives-warning">
            <AlertTriangle size={18} />
            <div>
              <strong>Important:</strong> Having more than 7 objectives/questions is not advisable. 
              Too many objectives create possible points of failure if one or more are not met, 
              which can weaken your entire research design.
            </div>
          </div>

          <div className="objectives-list">
            {objectives.map((obj, index) => (
              <div key={index} className="objective-input-row">
                <span className="objective-number">{index + 1}</span>
                <input
                  type="text"
                  value={obj}
                  onChange={(e) => handleObjectiveChange(index, e.target.value)}
                  placeholder={`Research objective/question ${index + 1}...`}
                  className="objective-input"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="constructs-extraction-section">
          <div className="section-header">
            <Puzzle size={20} />
            <h3>Extract Constructs</h3>
          </div>
          <p className="section-help">
            Based on your aim and objectives above, identify the key constructs (variables/concepts) 
            and assign each a type.
          </p>

          <div className="auto-populate-section">
            <button 
              className="auto-populate-btn"
              onClick={autoPopulateConstructs}
              disabled={filledObjectivesCount === 0}
            >
              <RefreshCw size={18} />
              Auto-populate from Objectives ({filledObjectivesCount})
            </button>
            <span className="auto-populate-hint">
              This will create constructs from your objectives above. You can then edit or delete them.
            </span>
          </div>

          <div className="add-construct-form">
            <p className="form-label">Or add a construct manually:</p>
            <div className="construct-input-row">
              <input
                type="text"
                value={newConstruct.name}
                onChange={(e) => setNewConstruct({ ...newConstruct, name: e.target.value })}
                placeholder="Enter construct name (e.g., Employee Motivation)"
                className="construct-name-input"
                onKeyPress={(e) => e.key === 'Enter' && newConstruct.name.trim() && addConstruct()}
              />
              <select
                value={newConstruct.type}
                onChange={(e) => setNewConstruct({ ...newConstruct, type: e.target.value })}
                className="construct-type-select"
              >
                <option value="independent">Independent Variable</option>
                <option value="dependent">Dependent Variable</option>
                <option value="mediator">Mediator</option>
                <option value="moderator">Moderator</option>
                <option value="control">Control Variable</option>
              </select>
              <button 
                className="add-btn" 
                onClick={addConstruct} 
                disabled={!newConstruct.name.trim()}
              >
                <Plus size={18} />
                Add
              </button>
            </div>
          </div>

          {constructs.length > 0 && (
            <div className="constructs-list">
              <h4>Your Constructs ({constructs.length})</h4>
              <div className="constructs-table expanded">
                <div className="constructs-table-header">
                  <span>Construct Name</span>
                  <span>Definition</span>
                  <span>Type</span>
                  <span></span>
                </div>
                {constructs.map((c) => (
                  <div key={c.id} className={`construct-row type-${c.type}`}>
                    <input
                      type="text"
                      value={c.name}
                      onChange={(e) => updateConstructName(c.id, e.target.value)}
                      className="construct-name-editable"
                      placeholder="Construct name..."
                    />
                    <input
                      type="text"
                      value={c.definition || ''}
                      onChange={(e) => updateConstructDefinition(c.id, e.target.value)}
                      className="construct-definition-editable"
                      placeholder="Add definition (required for validation)..."
                    />
                    <select
                      value={c.type}
                      onChange={(e) => updateConstructType(c.id, e.target.value)}
                      className="construct-type-dropdown"
                    >
                      <option value="independent">Independent Variable</option>
                      <option value="dependent">Dependent Variable</option>
                      <option value="mediator">Mediator</option>
                      <option value="moderator">Moderator</option>
                      <option value="control">Control Variable</option>
                    </select>
                    <button className="remove-btn" onClick={() => removeConstruct(c.id)}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="tip-box">
            <Lightbulb size={18} />
            <div>
              <strong>Tip:</strong> A good framework typically has 3-7 constructs. 
              Review your objectives above and identify the main variables or concepts mentioned.
            </div>
          </div>
        </div>
      </div>

      <NavigationButtons
        onContinue={handleContinue}
        canContinue={constructs.length >= 2}
        continueLabel="Continue to Levels"
      />
    </div>
  );
};

export default C1_ExtractConstructs;
